<script setup>
import {
  computed,
  onMounted,
  useTemplateRef,
  ref,
  watch,
  getCurrentInstance,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { downloadAttachment } from 'shared/helpers/downloadAttachment';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useExpandableContent } from 'shared/composables/useExpandableContent';
import { tryUseMessageContext } from '../provider.js';
import { MESSAGE_TYPES } from '../constants';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
  allowTranscribe: {
    type: Boolean,
    default: true,
  },
});

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();
const store = useStore();
const messageContext = tryUseMessageContext();
const isTranscribing = ref(false);

const transcribedText = computed(() =>
  (props.attachment.transcribedText || '').trim()
);
const transcriptionFailed = computed(
  () => props.attachment.transcriptionStatus === 'failed'
);
const isIncoming = computed(
  () => messageContext?.messageType?.value === MESSAGE_TYPES.INCOMING
);
const showTranscript = computed(
  () => Boolean(transcribedText.value) && props.showTranscribedText
);
const showTranscribeAction = computed(
  () =>
    props.allowTranscribe &&
    isIncoming.value &&
    !transcribedText.value &&
    Boolean(messageContext?.conversationId?.value)
);
const showTranscriptPanel = computed(
  () =>
    showTranscript.value ||
    showTranscribeAction.value ||
    isTranscribing.value
);

const { contentElement, showReadMore, showReadLess, toggleExpanded } =
  useExpandableContent({
    maxLines: 3,
    useResizeObserverForCheck: true,
  });

watch(transcribedText, text => {
  if (text) isTranscribing.value = false;
});

const transcribeAudio = async () => {
  if (isTranscribing.value) return;

  const conversationId = messageContext?.conversationId?.value;
  const messageId = messageContext?.id?.value || props.attachment.messageId;
  if (!conversationId || !messageId) return;

  isTranscribing.value = true;
  try {
    await store.dispatch('transcribeMessage', { conversationId, messageId });
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      t('CONVERSATION.AUDIO_TRANSCRIPTION.ERROR');
    useAlert(errorMessage);
  } finally {
    isTranscribing.value = false;
  }
};

const timeStampURL = computed(() => {
  return timeStampAppendedURL(props.attachment.dataUrl);
});

const audioType = computed(() => {
  const extension = props.attachment.extension?.toLowerCase();
  const url = props.attachment.dataUrl?.toLowerCase();

  if (extension === '.mp3' || extension === 'mp3') return 'audio/mpeg';
  if (extension === '.wav' || extension === 'wav') return 'audio/wav';
  if (extension === '.ogg' || extension === 'ogg' || extension === '.oga')
    return 'audio/ogg';
  if (extension === '.m4a' || extension === 'm4a') return 'audio/mp4';

  if (url?.includes('.mp3')) return 'audio/mpeg';
  if (url?.includes('.wav')) return 'audio/wav';
  if (url?.includes('.ogg') || url?.includes('.oga')) return 'audio/ogg';
  if (url?.includes('.m4a')) return 'audio/mp4';

  return 'audio/mpeg';
});

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const isMuted = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(1);

const { uid } = getCurrentInstance();

const onLoadedMetadata = () => {
  duration.value = audioPlayer.value?.duration;
};

const playbackSpeedLabel = computed(() => {
  return `${playbackSpeed.value}x`;
});

// There maybe a chance that the audioPlayer ref is not available
// When the onLoadMetadata is called, so we need to set the duration
// value when the component is mounted
onMounted(() => {
  duration.value = audioPlayer.value?.duration;
  audioPlayer.value.playbackRate = playbackSpeed.value;
});

// Listen for global audio play events and pause if it's not this audio
useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  if (!time || Number.isNaN(time)) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const toggleMute = () => {
  audioPlayer.value.muted = !audioPlayer.value.muted;
  isMuted.value = audioPlayer.value.muted;
};

const onTimeUpdate = () => {
  currentTime.value = audioPlayer.value?.currentTime;
};

const seek = event => {
  const time = Number(event.target.value);
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

const playOrPause = () => {
  if (isPlaying.value) {
    audioPlayer.value.pause();
    isPlaying.value = false;
  } else {
    // Emit event to pause all other audio
    emitter.emit('pause_playing_audio', uid);
    audioPlayer.value.play();
    isPlaying.value = true;
  }
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  playbackSpeed.value = 1;
  audioPlayer.value.playbackRate = 1;
};

const changePlaybackSpeed = () => {
  const speeds = [1, 1.5, 2];
  const currentIndex = speeds.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % speeds.length;
  playbackSpeed.value = speeds[nextIndex];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const downloadAudio = async () => {
  const { fileType, dataUrl, extension } = props.attachment;
  await downloadAttachment({ url: dataUrl, type: fileType, extension });
};
</script>

<template>
  <audio
    ref="audioPlayer"
    controls
    class="hidden"
    playsinline
    @loadedmetadata="onLoadedMetadata"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
  >
    <source :src="timeStampURL" :type="audioType" />
  </audio>
  <div
    v-bind="$attrs"
    class="rounded-xl w-full gap-2 p-1.5 bg-n-alpha-white flex flex-col items-center border border-n-container shadow-[0px_2px_8px_0px_rgba(94,94,94,0.06)]"
  >
    <div class="flex gap-1 w-full flex-1 items-center justify-start">
      <button class="p-0 border-0 size-8" @click="playOrPause">
        <Icon
          v-if="isPlaying"
          class="size-8"
          icon="i-teenyicons-pause-small-solid"
        />
        <Icon v-else class="size-8" icon="i-teenyicons-play-small-solid" />
      </button>
      <div class="tabular-nums text-xs">
        {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
      </div>
      <div class="flex-1 items-center flex px-2">
        <input
          type="range"
          min="0"
          :max="duration"
          :value="currentTime"
          class="w-full h-1 bg-n-slate-12/40 rounded-lg appearance-none cursor-pointer accent-current"
          @input="seek"
        />
      </div>
      <button
        class="border-0 w-10 h-6 grid place-content-center bg-n-alpha-2 hover:bg-alpha-3 rounded-2xl"
        @click="changePlaybackSpeed"
      >
        <span class="text-xs text-n-slate-11 font-medium">
          {{ playbackSpeedLabel }}
        </span>
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="toggleMute"
      >
        <Icon v-if="isMuted" class="size-4" icon="i-lucide-volume-off" />
        <Icon v-else class="size-4" icon="i-lucide-volume-2" />
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="downloadAudio"
      >
        <Icon class="size-4" icon="i-lucide-download" />
      </button>
    </div>

    <div
      v-if="showTranscriptPanel"
      class="flex flex-col gap-1.5 w-full px-2 pb-1.5 pt-2 border-t border-n-slate-12/10"
    >
      <div
        v-if="isTranscribing"
        class="flex items-center gap-1.5 text-xs text-n-slate-11"
      >
        <Spinner :size="14" />
        {{ $t('CONVERSATION.AUDIO_TRANSCRIPTION.TRANSCRIBING') }}
      </div>
      <button
        v-else-if="showTranscribeAction"
        type="button"
        class="flex items-center gap-1.5 self-start text-xs font-medium text-n-slate-11 hover:text-n-slate-12 bg-transparent border-0 p-0 cursor-pointer"
        @click.stop="transcribeAudio"
      >
        <Icon class="size-3.5" icon="i-lucide-file-text" />
        {{
          transcriptionFailed
            ? $t('CONVERSATION.AUDIO_TRANSCRIPTION.RETRY')
            : $t('CONVERSATION.AUDIO_TRANSCRIPTION.ACTION')
        }}
      </button>
      <div
        v-else-if="showTranscript"
        class="text-sm text-n-slate-12 leading-relaxed break-words w-full"
      >
        <p ref="contentElement" :class="{ 'line-clamp-3': showReadMore }">
          {{ transcribedText }}
        </p>
        <button
          v-if="showReadMore"
          type="button"
          class="text-xs text-n-slate-11 underline cursor-pointer bg-transparent border-0 p-0 hover:text-n-slate-12 font-medium mt-1"
          @click.stop="toggleExpanded(true)"
        >
          {{ $t('CONVERSATION.AUDIO_TRANSCRIPTION.READ_MORE') }}
        </button>
        <button
          v-if="showReadLess"
          type="button"
          class="text-xs text-n-slate-11 underline cursor-pointer bg-transparent border-0 p-0 hover:text-n-slate-12 font-medium mt-1"
          @click.stop="toggleExpanded(false)"
        >
          {{ $t('CONVERSATION.AUDIO_TRANSCRIPTION.READ_LESS') }}
        </button>
      </div>
    </div>
  </div>
</template>
